import type { CSSProperties, FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ListingPlaceholder } from '@/types/marketplace'

import {
    Activity,
    ArrowLeftRight,
    AtSign,
    Bitcoin,
    Briefcase,
    Building2,
    Calendar,
    Car,
    Code,
    Coins,
    Dumbbell,
    Gauge,
    Globe,
    Headphones,
    House,
    KeyRound,
    Lamp,
    Laptop,
    MessageCircle,
    Monitor,
    Music,
    Package,
    Shirt,
    ShoppingBag,
    Smartphone,
    Sofa,
    Sparkles,
    Tag,
    Ticket,
    Trophy,
    Users,
    Video,
    Wrench
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { patternStyle, resolvePlaceholder, themeForCategorySlug } from '@/lib/marketplace/enrichment/themes'

/**
 * THE placeholder art for a listing with no photograph.
 *
 * One component, used by every surface that shows a listing image, so a
 * photo-less listing looks the same in a grid, on its own page, on a seller
 * profile and in an operator's table. The priority is the same everywhere and
 * is expressed by the CALLER, in one shape:
 *
 *     uploaded image  ->  saved smart placeholder  ->  generic fallback
 *
 * The first two are the caller's `imageUrl` check; the third is this component
 * with nothing saved, which still draws designed art rather than an empty box
 * because `resolvePlaceholder` derives a stable appearance from the seed.
 *
 * Nothing here is generated at render time and nothing calls a model. The
 * values were decided once, when the listing was created.
 */

/**
 * Explicit map, mirroring `category-icon.tsx`: only the icons actually in the
 * curated system are bundled, and an unknown name degrades to a neutral tag
 * rather than rendering nothing.
 */
const ICONS: Record<string, LucideIcon> = {
    Activity,
    ArrowLeftRight,
    AtSign,
    Bitcoin,
    Briefcase,
    Building2,
    Calendar,
    Car,
    Code,
    Coins,
    Dumbbell,
    Gauge,
    Globe,
    Headphones,
    House,
    KeyRound,
    Lamp,
    Laptop,
    MessageCircle,
    Monitor,
    Music,
    Package,
    Shirt,
    ShoppingBag,
    Smartphone,
    Sofa,
    Sparkles,
    Tag,
    Ticket,
    Trophy,
    Users,
    Video,
    Wrench
}

export interface ListingPlaceholderArtProps {
    /** What enrichment saved on the listing. Null on a row that predates it. */
    placeholder?: ListingPlaceholder | null
    /** Falls back to the listing's category when no theme was saved. */
    categorySlug?: string | null
    /**
     * Stable per listing, so the same listing always draws the same art. The
     * listing id or slug; never a value that changes between renders.
     */
    seed: string
    /** The small printed word. Omit it on a thumbnail, where it cannot be read. */
    showLabel?: boolean
    /** Icon size, in Tailwind sizing utilities. */
    iconClassName?: string
    className?: string
}

export const ListingPlaceholderArt: FC<ListingPlaceholderArtProps> = ({
    placeholder,
    categorySlug,
    seed,
    showLabel = true,
    iconClassName,
    className
}): ReactNode => {
    const resolved = resolvePlaceholder({
        theme: placeholder?.theme ?? themeForCategorySlug(categorySlug),
        icon: placeholder?.icon ?? null,
        palette: placeholder?.palette ?? null,
        pattern: placeholder?.pattern ?? null,
        seed
    })

    const Icon = ICONS[resolved.icon] ?? Package
    const { colors } = resolved

    /**
     * Inline styles rather than utilities, because the colours ARE the data:
     * this is the runtime-computed case the design system reserves `style` for.
     */
    const plate: CSSProperties = { backgroundColor: colors.base, color: colors.ink }
    const texture: CSSProperties = { ...patternStyle(resolved.pattern, colors), opacity: 0.6 }

    return (
        <div
            aria-hidden='true'
            style={plate}
            className={cn('relative flex size-full items-center justify-center overflow-hidden', className)}
        >
            <span aria-hidden='true' className='absolute inset-0' style={texture} />

            {/* The plate the mark sits on, so the icon never fights the texture. */}
            <span
                aria-hidden='true'
                className='absolute left-1/2 top-1/2 size-[42%] min-h-[54px] min-w-[54px] -translate-x-1/2 -translate-y-1/2 rounded-full'
                style={{ backgroundColor: colors.base, opacity: 0.92 }}
            />

            <Icon
                className={cn('relative size-10 opacity-80', iconClassName)}
                strokeWidth={1.5}
                aria-hidden='true'
            />

            {showLabel && (
                <span
                    className='absolute bottom-3 left-3 rounded-[5px] px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-[0.08em] opacity-70'
                    style={{ backgroundColor: colors.wash, color: colors.ink }}
                >
                    {resolved.label}
                </span>
            )}
        </div>
    )
}
